import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchSharedFile, type SharedFileBody } from '../../../lib/file-shares';
import { sharedFileView } from '../../../lib/shared-file-view';
import { reportClientError } from '../../../lib/report-client-error';
import { DocViewerModal } from '../doc-viewer/DocViewerModal';

/**
 * Opens a file SOMEBODY ELSE shared with you, by its id.
 *
 * The one place on web that turns a `fileId` into something a person can read, and it exists because
 * two surfaces need it and neither could do it:
 *
 *  - the **share notification row** — an outreach that announces a file (`Outreach.shared_file`). Its
 *    action used to be "Reply", because nothing on the wire said the row was about a file at all; the
 *    reader's actual intent is to open it.
 *  - **Shared with you** — `SharedWithYou` has always rendered an "Open" button and was mounted with no
 *    `onOpen` handler, so pressing it did nothing.
 *
 * ## Nothing is copied, and that is the point
 *
 * The bytes are fetched through `/api/shared-file`, which re-checks the grant on every call and answers
 * `available: false` when the file was moved, deleted or the share revoked. So a withdrawn file says so
 * the moment it is opened, rather than being served from something we cached at notification time — and
 * `available: false` is an ORDINARY answer here, rendered as a sentence, never as a failure state.
 *
 * `kind` is resolved by the CALLER of `DocViewerModal` by contract (see its own docstring: a
 * `WorkspaceFile` carries only name + content, so re-deriving inside would drop the mime). We have all
 * three inputs — name, content and the server's `contentType` — so this is the right place to ask.
 */
export function SharedFileViewer({
  fileId,
  fileName: knownName,
  onClose,
}: {
  fileId: string;
  /** The name the OPENER already knows, shown while the read is in flight. Optional because not every
   *  caller has one (the "Shared with you" row does, a deeplink would not). */
  fileName?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<{ status: 'loading' } | { status: 'ready'; body: SharedFileBody } | { status: 'error' }>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await fetchSharedFile(fileId);
        if (!cancelled) setState({ status: 'ready', body });
      } catch (error) {
        reportClientError('handler', error, 'shared-file');
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    // Cancelled rather than aborted: the read is cheap and a stale resolve setting state on an unmounted
    // viewer is the only failure worth preventing here.
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // Every decision below is `sharedFileView`'s, in its own pure module so a test can CALL it — this
  // package's jest has no DOM and rendering the real viewer would drag the app graph. The component's
  // only remaining job is to hand the result to the modal.
  const view = sharedFileView(state, knownName, {
    loading: t('common.loading'),
    unavailableTitle: t('files.shared.unavailableTitle'),
    gone: t('files.shared.gone'),
    readFailed: t('files.shared.readFailed'),
  });

  return (
    <DocViewerModal
      file={{ name: view.name, isApp: false, content: view.content }}
      kind={view.kind}
      pageName="shared-file"
      // Read-only by construction: no `startEditing`, no `directory`, no `onEdit`. The file is the
      // owner's and this reader holds a grant to READ it — offering a save would promise a write the
      // share service refuses.
      onClose={onClose}
    />
  );
}
